/**
 * PRODUCTS
 *
 * One sidebar entry, two tabs: the catalogue itself and the on-site sales it generates.
 * They belong together — a product is defined here and its QR sales are reported here — so
 * a second top-level menu item would have split one job across two places.
 *
 * The active tab lives in the URL (?tab=sales) so it can be linked, bookmarked and survives
 * a refresh. That also lets the old /onsite-sales route redirect straight to the right tab.
 */
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingBagIcon, TagIcon } from "@heroicons/react/24/outline";

import { TwContainer, TwMainLayout, TwTabContainer } from "../../components/tailwind";

import ProductsList from "./ProductsList";
import OnsiteSalesList from "./OnsiteSalesList";

const TAB_CATALOGUE = "catalogue";
const TAB_SALES = "sales";

export default function ProductsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get("tab");
  const selectedTab = requestedTab === TAB_SALES ? TAB_SALES : TAB_CATALOGUE;

  const handleTabChange = (key) => {
    if (!key || key === selectedTab) return;
    // replace rather than push: switching tabs shouldn't fill the back button with history.
    setSearchParams(key === TAB_SALES ? { tab: TAB_SALES } : {}, { replace: true });
  };

  return (
    <TwMainLayout
      headTitle={t("pageTitle.myProducts", "My Products")}
      children={
        <TwContainer className="md:!px-0 py-0">
          <TwTabContainer
            tabsClassName="border-b px-5 md:px-10"
            selectedTab={selectedTab}
            setSelectedTab={handleTabChange}
            tabs={[
              {
                name: t("products.catalogue", "Catalogue"),
                key: TAB_CATALOGUE,
                icon: TagIcon,
                tabPanelComponent: (
                  <div className="px-5 md:px-10 py-6">
                    <ProductsList />
                  </div>
                ),
              },
              {
                name: t("pageTitle.onsiteSales", "On-site sales"),
                key: TAB_SALES,
                icon: ShoppingBagIcon,
                tabPanelComponent: (
                  <div className="px-5 md:px-10 py-6">
                    <OnsiteSalesList />
                  </div>
                ),
              },
            ]}
          />
        </TwContainer>
      }
    />
  );
}
